/**
 * 🎧 Web Audio Engine for Medley
 * High-fidelity audio processing with client-side effects
 * 
 * Features:
 * - No server-side compression during preview
 * - Real-time Power Stop (vinyl brake) effect
 * - Precision hard cut mechanism
 * - BPM-synced crossfade
 */

import { useRef, useCallback, useState, useEffect } from 'react';

const useWebAudioEngine = () => {
  const audioContextRef = useRef(null);
  const track1SourceRef = useRef(null);
  const track2SourceRef = useRef(null);
  const track1GainRef = useRef(null);
  const track2GainRef = useRef(null);
  const track1BufferRef = useRef(null);
  const track2BufferRef = useRef(null);
  
  const [isEngineReady, setIsEngineReady] = useState(false);
  const [track1Loaded, setTrack1Loaded] = useState(false);
  const [track2Loaded, setTrack2Loaded] = useState(false);

  // Initialize Audio Context with native sample rate (no resampling = no pitch shift)
  const initEngine = useCallback(() => {
    if (!audioContextRef.current) {
      // Use default sample rate from hardware to avoid resampling artifacts
      // This ensures playback matches the original file speed exactly
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
        // Let browser use native sample rate - no forced resampling
        latencyHint: 'playback'
      });
      
      // Create gain nodes for volume control (NO compression, NO processing)
      track1GainRef.current = audioContextRef.current.createGain();
      track2GainRef.current = audioContextRef.current.createGain();
      
      // Direct connection to destination - no intermediate processing
      track1GainRef.current.connect(audioContextRef.current.destination);
      track2GainRef.current.connect(audioContextRef.current.destination);
      
      setIsEngineReady(true);
      console.log(`🎧 Web Audio Engine initialized @ ${audioContextRef.current.sampleRate}Hz (native)`);
    }
    
    // Resume if suspended (browser autoplay policy)
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    
    return audioContextRef.current;
  }, []);

  // Load audio file into buffer (high-fidelity, no compression)
  // decodeAudioData automatically resamples to context's sample rate
  // This is handled by the browser and maintains correct playback speed
  const loadTrack = useCallback(async (file, trackNumber) => {
    const ctx = initEngine();
    
    try {
      const arrayBuffer = await file.arrayBuffer();
      // decodeAudioData will resample to AudioContext's sample rate
      // This is the correct way - browser handles it without pitch shift
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
      
      if (trackNumber === 1) {
        track1BufferRef.current = audioBuffer;
        setTrack1Loaded(true);
        console.log(`✅ Track 1 loaded: ${audioBuffer.duration.toFixed(2)}s, ${audioBuffer.sampleRate}Hz (context: ${ctx.sampleRate}Hz)`);
      } else {
        track2BufferRef.current = audioBuffer;
        setTrack2Loaded(true);
        console.log(`✅ Track 2 loaded: ${audioBuffer.duration.toFixed(2)}s, ${audioBuffer.sampleRate}Hz (context: ${ctx.sampleRate}Hz)`);
      }
      
      return audioBuffer;
    } catch (error) {
      console.error(`Failed to load track ${trackNumber}:`, error);
      throw error;
    }
  }, [initEngine]);

  // Clear track from memory
  const clearTrack = useCallback((trackNumber) => {
    if (trackNumber === 1) {
      if (track1SourceRef.current) {
        try {
          track1SourceRef.current.stop();
        } catch (e) {}
        track1SourceRef.current.disconnect();
        track1SourceRef.current = null;
      }
      track1BufferRef.current = null;
      setTrack1Loaded(false);
      console.log('🗑️ Track 1 cleared from Web Audio Engine');
    } else {
      if (track2SourceRef.current) {
        try {
          track2SourceRef.current.stop();
        } catch (e) {}
        track2SourceRef.current.disconnect();
        track2SourceRef.current = null;
      }
      track2BufferRef.current = null;
      setTrack2Loaded(false);
      console.log('🗑️ Track 2 cleared from Web Audio Engine');
    }
  }, []);

  /**
   * 🛑 HARD CUT - Instant volume kill
   * Eliminates any audio ghosting from Track 1
   */
  const hardCutTrack1 = useCallback(() => {
    const ctx = audioContextRef.current;
    if (!ctx || !track1GainRef.current) return;
    
    const now = ctx.currentTime;
    
    // Immediate gain to 0 (hard cut)
    track1GainRef.current.gain.cancelScheduledValues(now);
    track1GainRef.current.gain.setValueAtTime(0, now);
    
    // Stop the source node
    if (track1SourceRef.current) {
      try {
        track1SourceRef.current.stop(now);
      } catch (e) {}
    }
    
    console.log('🛑 HARD CUT executed - Track 1 silenced immediately');
  }, []);

  /**
   * ⚡ POWER STOP - Vinyl Brake Effect (client-side)
   * Creates realistic turntable stop with pitch drop
   * 
   * @param {number} duration - Brake duration in seconds
   * @param {function} onComplete - Callback when brake finishes
   */
  const applyPowerStop = useCallback((duration = 0.8, onComplete) => {
    const ctx = audioContextRef.current;
    if (!ctx || !track1SourceRef.current) return;
    
    const now = ctx.currentTime;
    const endTime = now + duration;
    
    // Get the source's playbackRate parameter
    const source = track1SourceRef.current;
    
    // Exponential slowdown curve: 1.0 → 0.1
    // This creates authentic vinyl brake sound
    source.playbackRate.cancelScheduledValues(now);
    source.playbackRate.setValueAtTime(1.0, now);
    source.playbackRate.exponentialRampToValueAtTime(0.1, endTime);
    
    // Volume slightly dips during brake
    track1GainRef.current.gain.cancelScheduledValues(now);
    track1GainRef.current.gain.setValueAtTime(track1GainRef.current.gain.value, now);
    track1GainRef.current.gain.linearRampToValueAtTime(0.3, endTime);
    
    console.log(`⚡ Power Stop: ${duration}s vinyl brake starting`);
    
    // Schedule hard cut at end of brake
    setTimeout(() => {
      hardCutTrack1();
      if (onComplete) onComplete();
    }, duration * 1000);
    
  }, [hardCutTrack1]);

  /**
   * 🎵 Play section of track with Web Audio API
   * High-fidelity playback without server compression
   */
  const playTrack = useCallback((trackNumber, startTime = 0, options = {}) => {
    const ctx = initEngine();
    const buffer = trackNumber === 1 ? track1BufferRef.current : track2BufferRef.current;
    const gainNode = trackNumber === 1 ? track1GainRef.current : track2GainRef.current;
    
    if (!buffer) {
      console.error(`Track ${trackNumber} buffer not loaded`);
      return null;
    }
    
    // Stop existing source if playing
    const existingSource = trackNumber === 1 ? track1SourceRef.current : track2SourceRef.current;
    if (existingSource) {
      try {
        existingSource.stop();
      } catch (e) {}
    }
    
    // Create new source node
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(gainNode);
    
    // Set initial volume
    gainNode.gain.setValueAtTime(options.volume || 1.0, ctx.currentTime);
    
    // Set playback rate
    source.playbackRate.setValueAtTime(options.playbackRate || 1.0, ctx.currentTime);
    
    // Start playback
    const offset = Math.max(0, Math.min(startTime, buffer.duration));
    source.start(0, offset);
    
    if (trackNumber === 1) {
      track1SourceRef.current = source;
    } else {
      track2SourceRef.current = source;
    }
    
    console.log(`▶️ Track ${trackNumber} playing from ${offset.toFixed(2)}s`);
    
    return source;
  }, [initEngine]);

  /**
   * Stop track playback
   */
  const stopTrack = useCallback((trackNumber) => {
    const source = trackNumber === 1 ? track1SourceRef.current : track2SourceRef.current;
    const gainNode = trackNumber === 1 ? track1GainRef.current : track2GainRef.current;
    
    if (source) {
      try {
        source.stop();
      } catch (e) {}
    }
    
    if (gainNode) {
      gainNode.gain.setValueAtTime(0, audioContextRef.current?.currentTime || 0);
    }
    
    console.log(`⏹️ Track ${trackNumber} stopped`);
  }, []);

  /**
   * Set track volume with optional fade
   */
  const setVolume = useCallback((trackNumber, volume, fadeTime = 0) => {
    const ctx = audioContextRef.current;
    const gainNode = trackNumber === 1 ? track1GainRef.current : track2GainRef.current;
    
    if (!ctx || !gainNode) return;
    
    const now = ctx.currentTime;
    const clampedVolume = Math.max(0, Math.min(1, volume));
    
    if (fadeTime > 0) {
      gainNode.gain.cancelScheduledValues(now);
      gainNode.gain.setValueAtTime(gainNode.gain.value, now);
      gainNode.gain.linearRampToValueAtTime(clampedVolume, now + fadeTime);
    } else {
      gainNode.gain.setValueAtTime(clampedVolume, now);
    }
  }, []);

  /**
   * Get buffer duration
   */
  const getDuration = useCallback((trackNumber) => {
    const buffer = trackNumber === 1 ? track1BufferRef.current : track2BufferRef.current;
    return buffer ? buffer.duration : 0;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return {
    initEngine,
    loadTrack,
    clearTrack,
    playTrack,
    stopTrack,
    setVolume,
    getDuration,
    applyPowerStop,
    hardCutTrack1,
    isEngineReady,
    track1Loaded,
    track2Loaded,
    audioContext: audioContextRef.current
  };
};

export default useWebAudioEngine;
